<script setup lang="ts">
import { Camera } from '@lucide/vue'
import maplibregl from 'maplibre-gl'
import {
  inject,
  watch,
  onMounted,
  onUnmounted,
  ref,
  createVNode,
  render,
  computed,
  nextTick,
  type Ref,
  type ShallowRef,
} from 'vue'

import { resolvePhotoUrl } from '@/services/offlinePhotoResolver'
import {
  ensureWorkspacePhotoAssetUrlsFresh,
  PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
} from '@/services/photoAssetUrlService'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Photo } from '@/types/photo'
import { createMapMarker } from '@/utils/mapMarkerElement'
import { Z_INDEX } from '@/utils/zIndex'

const emit = defineEmits<{
  'view-photo': [photoId: string]
}>()

const photoStore = usePhotoStore()
const uiStore = useUiStore()
const workspaceStore = useWorkspaceStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()

const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded')
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

const sourceId = computed(() => 'photo-cluster-source')
const clustersLayerId = computed(() => 'photo-clusters')
const clusterCountLayerId = computed(() => 'photo-cluster-count')
const unclusteredPointLayerId = computed(() => 'photo-unclustered-point')

type MarkerOffset = [number, number]

type ClusterDescriptor = {
  key: string
  kind: 'cluster'
  coords: [number, number]
  offset: MarkerOffset
  clusterId: number
  count: number
  imageUrl: string | null
}

type PhotoDescriptor = {
  key: string
  kind: 'photo'
  coords: [number, number]
  offset: MarkerOffset
  photoId: string
  zIndex: number
  imageUrl: string | null
  alt: string
}

type MarkerDescriptor = ClusterDescriptor | PhotoDescriptor

type ClusterMarkerEntry = {
  key: string
  kind: 'cluster'
  marker: maplibregl.Marker
  element: HTMLElement
  frameEl: HTMLElement
  badgeEl: HTMLElement
  imageEl: HTMLImageElement | null
  descriptor: ClusterDescriptor
  cleanup: () => void
}

type PhotoMarkerEntry = {
  key: string
  kind: 'photo'
  marker: maplibregl.Marker
  element: HTMLElement
  frameEl: HTMLElement
  imageEl: HTMLImageElement | null
  descriptor: PhotoDescriptor
  cleanup: () => void
}

type MarkerEntry = ClusterMarkerEntry | PhotoMarkerEntry

const markerRegistry = new Map<string, MarkerEntry>()

// Track current render token to cancel outdated async work
let activeRenderToken = 0

// Cache cluster preview thumbnails until the underlying photo data changes
type ClusterPreview = {
  photoId: string | null
  imageUrl: string | null
  datasetVersion: number
}
const clusterPreviewCache = new Map<string, ClusterPreview>()

// Photo dataset version increments whenever photo store data changes
let photoDatasetVersion = 0

let markerUpdateQueued = false
let markerUpdateInProgress = false
let pendingAnimationFrameId: number | null = null
let photoAssetRefreshIntervalId: number | null = null

const getGeoJsonData = (): GeoJSON.FeatureCollection => {
  return {
    type: 'FeatureCollection',
    features: photoStore.photos
      .filter(
        (photo: Photo) =>
          photo.hasCoordinates && typeof photo.lat === 'number' && typeof photo.lon === 'number',
      )
      .map((photo: Photo) => ({
        type: 'Feature',
        properties: {
          id: photo.clientId,
          name: photo.name || 'Photo',
          takenAt: typeof photo.takenAt === 'number' ? photo.takenAt : null,
        },
        geometry: {
          type: 'Point',
          coordinates: [photo.lon!, photo.lat!],
        },
      })),
  } as GeoJSON.FeatureCollection
}

// Build a 48x48 invisible touch target centered on (0,0)
const createTouchArea = () => {
  const touchArea = document.createElement('div')
  touchArea.className = 'touch-area'
  touchArea.style.position = 'absolute'
  touchArea.style.width = '48px'
  touchArea.style.height = '48px'
  touchArea.style.top = '-24px'
  touchArea.style.left = '-24px'
  touchArea.style.borderRadius = '50%'
  touchArea.style.cursor = 'pointer'
  touchArea.style.background = 'transparent'
  return touchArea
}

// Create thumbnail marker frame
const createThumbnailFrame = (size = 44) => {
  const frame = document.createElement('div')
  frame.style.position = 'absolute'
  frame.style.top = `-${size / 2}px`
  frame.style.left = `-${size / 2}px`
  frame.style.width = `${size}px`
  frame.style.height = `${size}px`
  frame.style.transition = 'transform 120ms ease, box-shadow 120ms ease'
  frame.className = 'rounded-md ring-2 ring-white shadow-md bg-gray-200'
  return frame
}

const applyPhotoSelectionStyle = (entry: PhotoMarkerEntry) => {
  const isBatchSelected =
    (mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
      mobileBatchSelectionStore.activeSurface === 'pinned') &&
    mobileBatchSelectionStore.photos.has(entry.descriptor.photoId)

  entry.frameEl.classList.toggle('ring-white', !isBatchSelected)
  entry.frameEl.classList.toggle('ring-blue-500', isBatchSelected)
  entry.frameEl.classList.toggle('shadow-md', !isBatchSelected)
  entry.frameEl.classList.toggle('shadow-lg', isBatchSelected)
  entry.frameEl.style.transform = isBatchSelected ? 'scale(1.12)' : 'scale(1)'
  entry.frameEl.style.boxShadow = isBatchSelected
    ? '0 10px 18px rgba(15, 23, 42, 0.2), 0 0 0 2px rgba(59, 130, 246, 0.55)'
    : ''
}

const updatePhotoSelectionStyles = () => {
  markerRegistry.forEach((entry) => {
    if (entry.kind === 'photo') {
      applyPhotoSelectionStyle(entry)
    }
  })
}

// Render a camera icon fallback inside a frame
const renderCameraFallback = (container: HTMLElement) => {
  const iconWrapper = document.createElement('div')
  iconWrapper.dataset.role = 'thumbnail-fallback'
  iconWrapper.className = 'w-full h-full grid place-items-center bg-blue-500'
  const iconEl = document.createElement('div')
  iconEl.dataset.role = 'thumbnail-fallback-icon'
  iconEl.className = 'text-white'
  const vNode = createVNode(Camera, {
    size: 18,
    stroke: 'currentColor',
    strokeWidth: 1,
    absoluteStrokeWidth: true,
  })
  render(vNode, iconEl)
  iconWrapper.appendChild(iconEl)
  container.appendChild(iconWrapper)
  return { wrapper: iconWrapper, iconEl }
}

// Single photo marker element (thumbnail + border)
const createSinglePhotoMarkerElement = (descriptor: PhotoDescriptor) => {
  const container = document.createElement('div')
  container.dataset.markerKind = 'photo'
  container.className = 'photo-marker-container relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS.toString()
  container.style.cursor = 'pointer'
  // Hint smoother transforms (used by MapLibre for DOM markers)
  container.style.willChange = 'transform'
  container.style.transition = 'opacity 80ms ease-out'

  const touchArea = createTouchArea()
  const frame = createThumbnailFrame(44)
  frame.dataset.role = 'photo-frame'
  frame.style.pointerEvents = 'none'

  let imageEl: HTMLImageElement | null = null
  if (descriptor.imageUrl) {
    imageEl = document.createElement('img')
    imageEl.src = descriptor.imageUrl
    imageEl.setAttribute('decoding', 'async')
    imageEl.alt = descriptor.alt
    imageEl.className = 'w-full h-full object-cover rounded-md'
    imageEl.style.pointerEvents = 'none'
    frame.appendChild(imageEl)
  } else {
    renderCameraFallback(frame)
  }

  container.appendChild(touchArea)
  container.appendChild(frame)
  return { element: container, touchArea, frame, imageEl }
}

// Cluster marker element (thumbnail + count badge)
const createClusterMarkerElement = (descriptor: ClusterDescriptor) => {
  const container = document.createElement('div')
  container.dataset.markerKind = 'cluster'
  container.className = 'photo-cluster-marker relative'
  // Ensure cluster badge sits above single photo thumbnails
  container.style.zIndex = Z_INDEX.MAP_MARKERS_CLUSTER.toString()
  container.style.cursor = 'pointer'
  // Hint smoother transforms
  container.style.willChange = 'transform'
  container.style.transition = 'opacity 80ms ease-out'

  const touchArea = createTouchArea()
  const frame = createThumbnailFrame(44)
  frame.dataset.role = 'cluster-frame'
  frame.style.pointerEvents = 'none'

  let imageEl: HTMLImageElement | null = null
  if (descriptor.imageUrl) {
    imageEl = document.createElement('img')
    imageEl.src = descriptor.imageUrl
    imageEl.setAttribute('decoding', 'async')
    imageEl.alt = 'Cluster'
    imageEl.className = 'w-full h-full object-cover rounded-md'
    imageEl.style.pointerEvents = 'none'
    // Ensure the thumbnail sits below the badge within the frame
    imageEl.style.position = 'relative'
    imageEl.style.zIndex = '0'
    frame.appendChild(imageEl)
  } else {
    renderCameraFallback(frame)
  }

  // Modern badge: compact pill, white border, brand blue background
  // Position relative to the thumbnail frame (bottom-right)
  const badge = document.createElement('div')
  badge.className = [
    // Slight outside overlap of the frame corner
    'absolute -bottom-1 -right-1 px-1.5 min-w-[18px] h-[18px] rounded-full',
    'bg-[#0099E5] text-white text-[10px] leading-none font-semibold',
    'flex items-center justify-center border border-white shadow-sm',
  ].join(' ')
  badge.dataset.role = 'cluster-count'
  badge.textContent = String(descriptor.count)
  badge.style.pointerEvents = 'none'
  // Keep badge above internal thumbnail frame
  // Use a higher local z-index so it overlays the image inside the frame
  badge.style.zIndex = '1'

  container.appendChild(touchArea)
  container.appendChild(frame)
  frame.appendChild(badge)
  return { element: container, touchArea, frame, badge, imageEl }
}

const ensureThumbnailContent = (
  frame: HTMLElement,
  currentImage: HTMLImageElement | null,
  imageUrl: string | null,
  alt: string,
): HTMLImageElement | null => {
  const fallback = frame.querySelector('[data-role="thumbnail-fallback"]') as HTMLElement | null

  if (imageUrl) {
    if (!currentImage) {
      const img = document.createElement('img')
      img.className = 'w-full h-full object-cover rounded-md'
      img.style.pointerEvents = 'none'
      img.setAttribute('decoding', 'async')
      img.style.position = 'relative'
      img.style.zIndex = '0'
      frame.appendChild(img)
      currentImage = img
    }
    if (currentImage.src !== imageUrl) {
      currentImage.src = imageUrl
    }
    if (currentImage.alt !== alt) {
      currentImage.alt = alt
    }
    if (fallback) {
      const iconContainer = fallback.querySelector(
        '[data-role="thumbnail-fallback-icon"]',
      ) as HTMLElement | null
      if (iconContainer) {
        render(null, iconContainer)
      }
      fallback.remove()
    }
    return currentImage
  }

  if (currentImage) {
    currentImage.remove()
    currentImage = null
  }
  if (!fallback) {
    renderCameraFallback(frame)
  }
  return null
}

const createClusterMarkerEntry = (
  map: maplibregl.Map,
  descriptor: ClusterDescriptor,
  source: maplibregl.GeoJSONSource,
): ClusterMarkerEntry => {
  const { element, frame, badge, imageEl } = createClusterMarkerElement(descriptor)

  const marker = createMapMarker({ element, anchor: 'center', offset: descriptor.offset })
    .setLngLat(descriptor.coords)
    .addTo(map)

  const entry: ClusterMarkerEntry = {
    key: descriptor.key,
    kind: 'cluster',
    marker,
    element,
    frameEl: frame,
    badgeEl: badge,
    imageEl,
    descriptor,
    cleanup: () => {
      element.removeEventListener('click', handleMouseClick)
    },
  }

  const handleClick = (event: Event) => {
    if (uiStore.isTrackDrawingActive) return
    event.preventDefault()
    event.stopPropagation()

    uiStore.disableAutoViewportAdaptation()
    uiStore.collapseMobileBottomPanel()

    const { clusterId, coords } = entry.descriptor
    source
      .getClusterExpansionZoom(clusterId)
      .then((zoom: number) => {
        const bump = 2
        const maxZoom = map.getMaxZoom?.() ?? 22
        const targetZoom = Math.min(zoom + bump, maxZoom)
        map.easeTo({ center: coords, zoom: targetZoom })
      })
      .catch(() => {})
  }

  const handleMouseClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    handleClick(event)
  }

  const handleMouseEnter = () => {
    uiStore.suppressTourHover()
  }

  const handleMouseLeave = () => {
    uiStore.releaseTourHoverSuppression()
  }

  element.addEventListener('click', handleMouseClick)
  element.addEventListener('mouseenter', handleMouseEnter)
  element.addEventListener('mouseleave', handleMouseLeave)

  entry.cleanup = () => {
    element.removeEventListener('click', handleMouseClick)
    element.removeEventListener('mouseenter', handleMouseEnter)
    element.removeEventListener('mouseleave', handleMouseLeave)
    uiStore.releaseTourHoverSuppression()
  }

  return entry
}

const updateClusterMarkerEntry = (entry: ClusterMarkerEntry, descriptor: ClusterDescriptor) => {
  entry.descriptor = descriptor
  entry.marker.setLngLat(descriptor.coords)
  entry.marker.setOffset(descriptor.offset)
  if (entry.badgeEl.textContent !== String(descriptor.count)) {
    entry.badgeEl.textContent = String(descriptor.count)
  }
  entry.imageEl = ensureThumbnailContent(
    entry.frameEl,
    entry.imageEl,
    descriptor.imageUrl,
    'Cluster',
  )
  entry.element.style.opacity = '1'
  entry.element.style.pointerEvents = 'auto'
  delete entry.element.dataset.clusterHidden
}

const createPhotoMarkerEntry = (
  map: maplibregl.Map,
  descriptor: PhotoDescriptor,
): PhotoMarkerEntry => {
  const { element, frame, imageEl } = createSinglePhotoMarkerElement(descriptor)
  element.style.zIndex = descriptor.zIndex.toString()

  const marker = createMapMarker({ element, anchor: 'center', offset: descriptor.offset })
    .setLngLat(descriptor.coords)
    .addTo(map)

  const entry: PhotoMarkerEntry = {
    key: descriptor.key,
    kind: 'photo',
    marker,
    element,
    frameEl: frame,
    imageEl,
    descriptor,
    cleanup: () => {
      element.removeEventListener('click', handleMouseClick)
    },
  }

  const handleClick = (event: Event) => {
    if (uiStore.isTrackDrawingActive) return
    event.preventDefault()
    event.stopPropagation()
    if (
      mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
      mobileBatchSelectionStore.activeSurface === 'pinned'
    ) {
      mobileBatchSelectionStore.toggle('photos', entry.descriptor.photoId)
      return
    }
    emit('view-photo', entry.descriptor.photoId)
  }

  const handleMouseClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    handleClick(event)
  }

  const handleMouseEnter = () => {
    uiStore.suppressTourHover()
  }

  const handleMouseLeave = () => {
    uiStore.releaseTourHoverSuppression()
  }

  element.addEventListener('click', handleMouseClick)
  element.addEventListener('mouseenter', handleMouseEnter)
  element.addEventListener('mouseleave', handleMouseLeave)

  applyPhotoSelectionStyle(entry)

  entry.cleanup = () => {
    element.removeEventListener('click', handleMouseClick)
    element.removeEventListener('mouseenter', handleMouseEnter)
    element.removeEventListener('mouseleave', handleMouseLeave)
    uiStore.releaseTourHoverSuppression()
  }

  return entry
}

const updatePhotoMarkerEntry = (entry: PhotoMarkerEntry, descriptor: PhotoDescriptor) => {
  entry.descriptor = descriptor
  entry.marker.setLngLat(descriptor.coords)
  entry.marker.setOffset(descriptor.offset)
  if (entry.element.style.zIndex !== descriptor.zIndex.toString()) {
    entry.element.style.zIndex = descriptor.zIndex.toString()
  }
  entry.imageEl = ensureThumbnailContent(
    entry.frameEl,
    entry.imageEl,
    descriptor.imageUrl,
    descriptor.alt,
  )
  entry.element.style.opacity = '1'
  entry.element.style.pointerEvents = 'auto'
  applyPhotoSelectionStyle(entry)
}

const removeCustomMarkers = () => {
  markerRegistry.forEach((entry) => {
    entry.cleanup()
    entry.marker.remove()
  })
  markerRegistry.clear()
}

const cancelPendingMarkerWork = () => {
  if (pendingAnimationFrameId !== null) {
    cancelAnimationFrame(pendingAnimationFrameId)
    pendingAnimationFrameId = null
  }
  markerUpdateQueued = false
  markerUpdateInProgress = false
  activeRenderToken += 1
}

const forceRemoveMarkers = () => {
  cancelPendingMarkerWork()
  removeCustomMarkers()
}

defineExpose({
  forceRemoveMarkers,
})

const binaryObjectUrlCache = new Map<string, string>()
const resolvedThumbnailUrlCache = new Map<string, string>()

const refreshMarkerThumbnailUrlsIfNeeded = async (): Promise<boolean> => {
  if (!workspaceStore.clientId || photoStore.photos.length === 0) {
    return false
  }

  const refreshed = await ensureWorkspacePhotoAssetUrlsFresh(
    workspaceStore.clientId,
    photoStore.photos,
    {
      variants: ['thumb'],
    },
  )
  if (refreshed) {
    resolvedThumbnailUrlCache.clear()
    clusterPreviewCache.clear()
    queueMarkerUpdate()
  }
  return refreshed
}

// Helper function to get photo URL with fallbacks for thumbnails and full images
const getPhotoUrl = (photo: Photo): string => {
  const resolvedOffline = resolvedThumbnailUrlCache.get(photo.clientId)
  if (resolvedOffline) {
    return resolvedOffline
  }

  // First try thumbnail data (preferred for popups)
  if (photo._thumbnailData) {
    return photo._thumbnailData
  }

  // Try thumbnail URL
  if (photo._thumbnailUrl) {
    return photo._thumbnailUrl
  }

  // Fallback to full image data URL
  if (photo._binaryDataUrl) {
    return photo._binaryDataUrl
  }

  // Create URL from File object if available
  if (photo.binaryData instanceof File) {
    if (photo.clientId) {
      const cached = binaryObjectUrlCache.get(photo.clientId)
      if (cached) {
        return cached
      }
      const objectUrl = URL.createObjectURL(photo.binaryData)
      binaryObjectUrlCache.set(photo.clientId, objectUrl)
      return objectUrl
    }

    return URL.createObjectURL(photo.binaryData)
  }

  // Return empty string if no image data is available
  return ''
}

const resolveThumbnailUrl = async (photo: Photo): Promise<string | null> => {
  await refreshMarkerThumbnailUrlsIfNeeded()

  const cached = resolvedThumbnailUrlCache.get(photo.clientId)
  if (cached) {
    return cached
  }

  const rawUrl =
    photo._thumbnailData || photo._thumbnailUrl || photo._binaryDataUrl || getPhotoUrl(photo) || ''
  if (!rawUrl) {
    return null
  }

  const resolvedUrl = await resolvePhotoUrl(
    workspaceStore.clientId,
    photo.clientId,
    rawUrl,
    'thumb',
  )
  resolvedThumbnailUrlCache.set(photo.clientId, resolvedUrl)
  photo._resolvedOfflineThumbnailUrl = resolvedUrl
  return resolvedUrl
}

const addClusterSource = () => {
  const map = mapInstance?.value
  if (!map) return

  if (map.getSource(sourceId.value)) {
    const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
    source.setData(getGeoJsonData())
  } else {
    map.addSource(sourceId.value, {
      type: 'geojson',
      data: getGeoJsonData(),
      cluster: true,
      clusterMaxZoom: 14,
      // Bump radius a bit so MapLibre merges very close clusters.
      // Our DOM markers are ~44px wide, so 52px helps reduce overlaps.
      clusterRadius: 52,
    })
  }
}

// Ensure a one-shot refresh after the map finishes processing the source
const scheduleInitialMarkerSync = () => {
  const map = mapInstance?.value
  if (!map) return
  map.once('idle', () => {
    if (
      uiStore.arePhotosVisible &&
      !uiStore.isInWorkspacesOverview &&
      map.getSource(sourceId.value)
    ) {
      queueMarkerUpdate()
    }
  })
}

// Distance helper (Haversine) in meters
const metersBetween = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const toRad = (d: number) => (d * Math.PI) / 180
  const R = 6371000 // meters
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// Group photo thumbnails that are within a few meters of each other
const SAME_LOCATION_THRESHOLD_M = 60
const STACK_OFFSET_PX = 15 // pixel shift per item along 45°
// When multiple cluster features render within this many screen pixels,
// we fan them out using small radial offsets to reduce overlaps.
const CLUSTER_COLLISION_PX = 46
const CLUSTER_FAN_RADIUS_PX = 16

const createClusterLayers = () => {
  const map = mapInstance?.value
  if (!map) return

  // Add cluster layer (invisible but queryable)
  if (!map.getLayer(clustersLayerId.value)) {
    map.addLayer({
      id: clustersLayerId.value,
      type: 'circle',
      source: sourceId.value,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#0099E5', // Color irrelevant since hidden
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          18, // 18px circle for count < 10 (touch-friendly size)
          10,
          22, // 22px circle for count >= 10 and < 50 (touch-friendly size)
          50,
          26, // 26px circle for count >= 50 (touch-friendly size)
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
      },
    })
  }

  // Add cluster count text layer (invisible)
  if (!map.getLayer(clusterCountLayerId.value)) {
    map.addLayer({
      id: clusterCountLayerId.value,
      type: 'symbol',
      source: sourceId.value,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Noto Sans Medium'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#FFFFFF',
        'text-opacity': 0,
      },
    })
  }

  // Add unclustered point layer (invisible but queryable)
  if (!map.getLayer(unclusteredPointLayerId.value)) {
    map.addLayer({
      id: unclusteredPointLayerId.value,
      type: 'circle',
      source: sourceId.value,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#0099E5', // Color irrelevant since hidden
        'circle-radius': 10,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
      },
    })
  }
}

// No hover popups per new design

const updateLayerVisibility = () => {
  const map = mapInstance?.value
  if (!map) return

  // Hide photos when in workspaces overview mode or when map elements are force-hidden
  const visible =
    uiStore.arePhotosVisible &&
    !uiStore.areMapElementsForceHidden &&
    !uiStore.isInWorkspacesOverview

  // Also toggle hidden query layers so they don't consume resources when hidden
  const visibility = visible ? 'visible' : 'none'
  if (map.getLayer(clustersLayerId.value)) {
    map.setLayoutProperty(clustersLayerId.value, 'visibility', visibility)
  }
  if (map.getLayer(clusterCountLayerId.value)) {
    map.setLayoutProperty(clusterCountLayerId.value, 'visibility', visibility)
  }
  if (map.getLayer(unclusteredPointLayerId.value)) {
    map.setLayoutProperty(unclusteredPointLayerId.value, 'visibility', visibility)
  }

  if (visible) {
    queueMarkerUpdate()
  } else {
    forceRemoveMarkers()
  }
}

const removeLayers = () => {
  const map = mapInstance?.value
  if (!map) return

  if (map.getLayer(clusterCountLayerId.value)) {
    map.removeLayer(clusterCountLayerId.value)
  }

  if (map.getLayer(clustersLayerId.value)) {
    map.removeLayer(clustersLayerId.value)
  }

  if (map.getLayer(unclusteredPointLayerId.value)) {
    map.removeLayer(unclusteredPointLayerId.value)
  }

  if (map.getSource(sourceId.value)) {
    map.removeSource(sourceId.value)
  }
}

const waitForMapStability = async () => {
  await nextTick()
  const map = mapInstance?.value
  if (!map) return

  await new Promise<void>((resolve) => {
    if (!mapLoaded?.value) {
      resolve()
      return
    }

    let resolved = false
    let timeoutId: number | null = null
    let idleListenerAttached = false

    const finalize = () => {
      if (resolved) return
      resolved = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      if (idleListenerAttached) {
        map.off('idle', finalize)
      }
      resolve()
    }

    if (typeof map.areTilesLoaded === 'function' && map.areTilesLoaded()) {
      finalize()
      return
    }

    map.once('idle', finalize)
    idleListenerAttached = true
    timeoutId = window.setTimeout(finalize, 200)
  })

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

const collectMarkerDescriptors = async (token: number): Promise<MarkerDescriptor[]> => {
  const map = mapInstance?.value
  if (!map || !mapLoaded?.value) {
    return []
  }

  const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource | undefined
  if (!source) {
    return []
  }

  const descriptors: MarkerDescriptor[] = []
  const descriptorKeys = new Set<string>()
  const photoIndex = new Map<string, Photo>()

  photoStore.photos.forEach((photo: Photo) => {
    if (photo.clientId) {
      photoIndex.set(photo.clientId, photo)
    }
  })

  const clusterFeatures = map.queryRenderedFeatures({
    layers: [clustersLayerId.value],
  })
  const unclusteredFeatures = map.queryRenderedFeatures({
    layers: [unclusteredPointLayerId.value],
  })

  type ClusterNode = {
    key: string
    clusterId: number
    count: number
    coords: [number, number]
    screen: { x: number; y: number }
  }

  const nodes: ClusterNode[] = clusterFeatures
    .map((feature) => {
      if (!feature.properties) return null
      const clusterId = feature.properties.cluster_id as number
      const count = feature.properties.point_count as number
      const geometry = feature.geometry as GeoJSON.Point
      if (!geometry || geometry.type !== 'Point') return null
      const coords = geometry.coordinates as [number, number]
      const key = `cluster-${clusterId}`
      const projected = map.project({ lng: coords[0], lat: coords[1] } as maplibregl.LngLatLike)
      const screen = { x: projected.x, y: projected.y }
      return { key, clusterId, count, coords, screen }
    })
    .filter((node): node is ClusterNode => !!node)

  const distanceOnScreen = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y)

  const clusterGroups: ClusterNode[][] = []
  nodes.forEach((node) => {
    const group = clusterGroups.find(
      (existing) => distanceOnScreen(existing[0].screen, node.screen) <= CLUSTER_COLLISION_PX,
    )
    if (group) {
      group.push(node)
    } else {
      clusterGroups.push([node])
    }
  })

  for (const group of clusterGroups) {
    const ordered = [...group].sort((a, b) => b.count - a.count)
    const total = ordered.length
    const descriptorsForGroup = await Promise.all(
      ordered.map(async (node, index) => {
        if (token !== activeRenderToken) return null

        let offset: MarkerOffset = [0, 0]
        if (total > 1) {
          const angle = (2 * Math.PI * index) / total
          const dx = Math.round(CLUSTER_FAN_RADIUS_PX * Math.cos(angle))
          const dy = Math.round(CLUSTER_FAN_RADIUS_PX * Math.sin(angle))
          offset = [dx, dy]
        }

        const cacheKey = node.key
        let cached = clusterPreviewCache.get(cacheKey)
        if (cached && cached.datasetVersion !== photoDatasetVersion) {
          clusterPreviewCache.delete(cacheKey)
          cached = undefined
        }

        let imageUrl: string | null = cached?.imageUrl ?? null
        if (!cached) {
          try {
            const leafLimit = node.count <= 20 ? node.count : 1
            const leaves = await source.getClusterLeaves(node.clusterId, leafLimit, 0)
            if (token !== activeRenderToken) return null

            const leafPhotos: Photo[] = leaves
              .map((leaf) => {
                const id =
                  typeof leaf.properties?.id === 'string' ? (leaf.properties.id as string) : ''
                return id ? (photoIndex.get(id) ?? null) : null
              })
              .filter((p): p is Photo => !!p)

            let representative: Photo | undefined
            if (leafLimit > 1) {
              const timed = leafPhotos.filter((p) => typeof p.takenAt === 'number')
              if (timed.length > 0) {
                representative = timed.reduce((earliest, p) =>
                  p.takenAt! < earliest.takenAt! ? p : earliest,
                )
              } else if (leafPhotos.length > 0) {
                representative = leafPhotos[0]
              }
            } else if (leafPhotos.length > 0) {
              representative = leafPhotos[0]
            }

            if (representative) {
              const url = await resolveThumbnailUrl(representative)
              imageUrl = url || null
            } else {
              imageUrl = null
            }

            clusterPreviewCache.set(cacheKey, {
              photoId: representative?.clientId ?? null,
              imageUrl,
              datasetVersion: photoDatasetVersion,
            })
          } catch {
            // Keep existing cached image (if any) when cluster leaves retrieval fails
          }
        }

        const descriptor: ClusterDescriptor = {
          key: node.key,
          kind: 'cluster',
          coords: node.coords,
          offset,
          clusterId: node.clusterId,
          count: node.count,
          imageUrl,
        }
        return descriptor
      }),
    )

    descriptorsForGroup
      .filter((descriptor): descriptor is ClusterDescriptor => !!descriptor)
      .forEach((descriptor) => {
        if (descriptorKeys.has(descriptor.key)) return
        descriptorKeys.add(descriptor.key)
        descriptors.push(descriptor)
      })
  }

  type Item = { id: string; lon: number; lat: number; takenAt: number | null }

  const items: Item[] = unclusteredFeatures
    .map((feature) => {
      if (!feature.properties) return null
      const id = feature.properties.id as string
      if (!id) return null
      const geometry = feature.geometry as GeoJSON.Point
      if (!geometry || geometry.type !== 'Point') return null
      const coords = geometry.coordinates as [number, number]
      const takenAt = (feature.properties.takenAt as number | null) ?? null
      return { id, lon: coords[0], lat: coords[1], takenAt }
    })
    .filter((value): value is Item => !!value)

  const groups: { lon: number; lat: number; items: Item[] }[] = []
  items.forEach((item) => {
    let group = groups.find(
      (candidate) =>
        metersBetween(candidate.lat, candidate.lon, item.lat, item.lon) <=
        SAME_LOCATION_THRESHOLD_M,
    )
    if (!group) {
      group = { lon: item.lon, lat: item.lat, items: [] }
      groups.push(group)
    }
    group.items.push(item)
  })

  for (const group of groups) {
    const sorted = [...group.items].sort((a, b) => {
      if (a.takenAt == null && b.takenAt == null) return a.id.localeCompare(b.id)
      if (a.takenAt == null) return -1
      if (b.takenAt == null) return 1
      return a.takenAt - b.takenAt
    })

    for (const [index, item] of sorted.entries()) {
      const photo = photoIndex.get(item.id)
      if (!photo || !photo.hasCoordinates || photo.lat === undefined || photo.lon === undefined) {
        continue
      }

      const key = `photo-${item.id}`
      if (descriptorKeys.has(key)) {
        continue
      }

      const imageUrl = (await resolveThumbnailUrl(photo)) || null
      const descriptor: PhotoDescriptor = {
        key,
        kind: 'photo',
        coords: [group.lon, group.lat],
        offset: [index * STACK_OFFSET_PX, index * STACK_OFFSET_PX],
        photoId: item.id,
        zIndex: Z_INDEX.MAP_MARKERS + index,
        imageUrl,
        alt: photo.name || 'Photo',
      }
      descriptorKeys.add(key)
      descriptors.push(descriptor)
    }
  }

  return descriptors
}

const applyMarkerDescriptors = (descriptors: MarkerDescriptor[]) => {
  const map = mapInstance?.value
  if (!map) return

  const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource | undefined
  if (!source) return

  const nextKeys = new Set<string>()

  descriptors.forEach((descriptor) => {
    nextKeys.add(descriptor.key)
    const existing = markerRegistry.get(descriptor.key)

    if (existing) {
      if (existing.kind === 'cluster' && descriptor.kind === 'cluster') {
        updateClusterMarkerEntry(existing, descriptor)
      } else if (existing.kind === 'photo' && descriptor.kind === 'photo') {
        updatePhotoMarkerEntry(existing, descriptor)
      } else {
        existing.cleanup()
        existing.marker.remove()
        markerRegistry.delete(descriptor.key)
        const created =
          descriptor.kind === 'cluster'
            ? createClusterMarkerEntry(map, descriptor, source)
            : createPhotoMarkerEntry(map, descriptor)
        markerRegistry.set(descriptor.key, created)
      }
      return
    }

    const created =
      descriptor.kind === 'cluster'
        ? createClusterMarkerEntry(map, descriptor, source)
        : createPhotoMarkerEntry(map, descriptor)
    markerRegistry.set(descriptor.key, created)
  })

  const removals: string[] = []
  markerRegistry.forEach((entry, key) => {
    if (!nextKeys.has(key)) {
      entry.cleanup()
      entry.marker.remove()
      removals.push(key)
    }
  })

  removals.forEach((key) => markerRegistry.delete(key))
}

const flushQueuedMarkerUpdate = () => {
  if (markerUpdateInProgress) {
    return
  }

  if (!markerUpdateQueued) {
    return
  }

  markerUpdateQueued = false
  startMarkerUpdate()
}

const startMarkerUpdate = () => {
  if (
    !uiStore.arePhotosVisible ||
    uiStore.areMapElementsForceHidden ||
    uiStore.isInWorkspacesOverview
  ) {
    forceRemoveMarkers()
    return
  }

  markerUpdateInProgress = true
  const token = ++activeRenderToken

  waitForMapStability()
    .then(async () => {
      if (token !== activeRenderToken) {
        return [] as MarkerDescriptor[]
      }
      return collectMarkerDescriptors(token)
    })
    .then((descriptors) => {
      if (token === activeRenderToken && Array.isArray(descriptors)) {
        applyMarkerDescriptors(descriptors)
      }
    })
    .catch(() => {})
    .finally(() => {
      markerUpdateInProgress = false
      if (markerUpdateQueued) {
        queueMicrotask(flushQueuedMarkerUpdate)
      }
    })
}

const queueMarkerUpdate = () => {
  if (
    !uiStore.arePhotosVisible ||
    uiStore.areMapElementsForceHidden ||
    uiStore.isInWorkspacesOverview
  ) {
    forceRemoveMarkers()
    return
  }

  markerUpdateQueued = true
  if (pendingAnimationFrameId !== null) {
    return
  }

  pendingAnimationFrameId = window.requestAnimationFrame(() => {
    pendingAnimationFrameId = null
    flushQueuedMarkerUpdate()
  })
}

const hideExistingClusterMarkers = () => {
  markerRegistry.forEach((entry) => {
    if (entry.kind === 'cluster') {
      entry.element.dataset.clusterHidden = 'true'
      entry.element.style.opacity = '0'
      entry.element.style.pointerEvents = 'none'
    }
  })
}

const handleToolbarTransitionEnd = () => {
  if (
    uiStore.arePhotosVisible &&
    !uiStore.areMapElementsForceHidden &&
    !uiStore.isInWorkspacesOverview
  ) {
    queueMarkerUpdate()
  }
}

const eventHandlers = {
  moveend: null as (() => void) | null,
  zoomend: null as (() => void) | null,
  zoomstart: null as (() => void) | null,
}
const initializeLayers = () => {
  const map = mapInstance?.value
  const loaded = mapLoaded?.value

  if (!map || !loaded) {
    return
  }

  if (eventHandlers.moveend) {
    map.off('moveend', eventHandlers.moveend)
    eventHandlers.moveend = null
  }
  if (eventHandlers.zoomstart) {
    map.off('zoomstart', eventHandlers.zoomstart)
    eventHandlers.zoomstart = null
  }
  if (eventHandlers.zoomend) {
    map.off('zoomend', eventHandlers.zoomend)
    eventHandlers.zoomend = null
  }

  addClusterSource()
  createClusterLayers()
  updateLayerVisibility()
  // One-shot sync to ensure clusters are processed before first render
  scheduleInitialMarkerSync()

  if (
    uiStore.arePhotosVisible &&
    !uiStore.areMapElementsForceHidden &&
    !uiStore.isInWorkspacesOverview
  ) {
    queueMarkerUpdate()
  }

  eventHandlers.moveend = queueMarkerUpdate
  eventHandlers.zoomstart = hideExistingClusterMarkers
  eventHandlers.zoomend = queueMarkerUpdate
  map.on('moveend', eventHandlers.moveend)
  map.on('zoomstart', eventHandlers.zoomstart)
  map.on('zoomend', eventHandlers.zoomend)
}

onMounted(() => {
  if (mapInstance?.value && mapLoaded?.value) {
    initializeLayers()
  }
  void refreshMarkerThumbnailUrlsIfNeeded()
  photoAssetRefreshIntervalId = window.setInterval(
    () => void refreshMarkerThumbnailUrlsIfNeeded(),
    PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
  )
})

watch(
  mapLoaded ?? ref(false),
  (isLoaded) => {
    if (isLoaded && mapInstance?.value) {
      initializeLayers()
    }
  },
  { immediate: true },
)

watch(
  () => photoStore.photos,
  () => {
    photoDatasetVersion += 1
    clusterPreviewCache.clear()

    const map = mapInstance?.value
    if (map && mapLoaded?.value && map.getSource(sourceId.value)) {
      const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
      source.setData(getGeoJsonData())

      nextTick(() => {
        if (uiStore.arePhotosVisible && !uiStore.isInWorkspacesOverview) {
          queueMarkerUpdate()
        }
      })

      // Also schedule a one-shot render after tiles/cluster processing completes
      scheduleInitialMarkerSync()
    }
  },
  { deep: true },
)

watch(
  () => uiStore.arePhotosVisible,
  (newVisible, oldVisible) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      // If visibility changed from false to true and not in workspaces overview, ensure markers are recreated
      if (newVisible && !oldVisible && !uiStore.isInWorkspacesOverview) {
        nextTick(() => {
          queueMarkerUpdate()
        })
      }
    }
  },
)

watch(
  () => uiStore.areMapElementsForceHidden,
  (newHidden, oldHidden) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      if (!newHidden && oldHidden && uiStore.arePhotosVisible && !uiStore.isInWorkspacesOverview) {
        nextTick(() => {
          queueMarkerUpdate()
        })
      }
    }
    if (newHidden) {
      forceRemoveMarkers()
    }
  },
)

// Watch for workspaces overview state changes
watch(
  () => uiStore.isInWorkspacesOverview,
  (newInOverview, oldInOverview) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      // If leaving workspaces overview mode and photos are visible, recreate markers
      if (!newInOverview && oldInOverview && uiStore.arePhotosVisible) {
        nextTick(() => {
          queueMarkerUpdate()
        })
      }
      // If entering workspaces overview mode, remove markers
      else if (newInOverview && !oldInOverview) {
        forceRemoveMarkers()
      }
    }
  },
)

watch(
  () => uiStore.toolbarLayoutEpoch,
  () => {
    if (!mapInstance?.value || !mapLoaded?.value) {
      return
    }
    nextTick(() => {
      handleToolbarTransitionEnd()
    })
  },
  { flush: 'post' },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (!mapInstance?.value || !mapLoaded?.value) {
      return
    }

    initializeLayers()
  },
)

watch(
  () => [
    mobileBatchSelectionStore.activeSurface,
    [...mobileBatchSelectionStore.photos].sort().join('|'),
  ],
  () => {
    updatePhotoSelectionStyles()
  },
)

onUnmounted(() => {
  forceRemoveMarkers()
  if (photoAssetRefreshIntervalId !== null) {
    window.clearInterval(photoAssetRefreshIntervalId)
    photoAssetRefreshIntervalId = null
  }
  clusterPreviewCache.clear()
  binaryObjectUrlCache.forEach((url) => URL.revokeObjectURL(url))
  binaryObjectUrlCache.clear()
  resolvedThumbnailUrlCache.clear()

  // Remove event listeners before layers to prevent errors
  const map = mapInstance?.value
  if (map) {
    if (eventHandlers.moveend) map.off('moveend', eventHandlers.moveend)
    if (eventHandlers.zoomend) map.off('zoomend', eventHandlers.zoomend)
    if (eventHandlers.zoomstart) map.off('zoomstart', eventHandlers.zoomstart)
  }
  removeLayers()
})
</script>

<template>
  <div class="hidden"></div>
</template>

<style></style>
